import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import { TableHeader } from '../../../../containers/SingleSheetV2/table/Header'
import { TableRow } from '../../../../containers/SingleSheetV2/table/cells'
import { DateCell } from '../../../../containers/SingleSheetV2/table/cells/DateCell'
import { useKeyBind } from '../../../../hooks/useKeyBind'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { KEY_SAVE } from '../../../../model/keyBind'
import {
  DateV2,
  dateV2Service,
} from '../../../../../domain/value-object/DateV2'
import SubmitButton from '../button/SubmitButton'
import CloseButton from '../button/CloseButton'
import { useGetTaskActualWork } from '../../../../../applications/usecases/taskActualWork/getTaskActualWork'
import { UUIDV2 } from '../../../../../domain/value-object/UUIDV2'
import { useSaveTaskActualWork } from '../../../../../applications/usecases/taskActualWork/saveTaskActualWork'
import { useDispatch } from 'react-redux'
import { addScreenMessage, MessageLevel } from '../../../../../store/messages'
import { intl } from '../../../../../i18n'
import { TextCell } from '../../../../containers/SingleSheetV2/table/cells/TextCell'
import { useNumberParser } from '../../../../hooks/useNumberParser'
import { ErrorCode } from '../../../../../handlers/globalErrorHandler'
import store from '../../../../../store'
import { WbsItemTypeVO } from '../../../../../domain/value-object/WbsItemTypeVO'

type Props = {
  taskUuid: string
  projectUuid: string
  wbsItemType: WbsItemTypeVO
} & Omit<DialogState, 'openDialog'>
type DialogState = {
  open: boolean
  openDialog: () => void
  closeDialog: () => void
}

export const useTaskActualWorkRegistrationDialog = (): DialogState => {
  const [open, setOpen] = useState(false)
  const openDialog = useCallback(() => {
    setOpen(true)
  }, [])
  const closeDialog = useCallback(() => {
    setOpen(false)
  }, [])
  return { open, openDialog, closeDialog }
}
const TaskActualWorkRegistrationDialog = ({
  taskUuid,
  projectUuid,
  wbsItemType,
  open,
  closeDialog,
}: Props) => {
  const dispatch = useDispatch()
  const [processing, setProcessing] = useState(false)
  const [workDate, setWorkDate] = useState<DateV2 | undefined>(
    dateV2Service.today()
  )
  const [workHour, setWorkHour] = useState<string | undefined>('0')
  const [isComposing, setIsComposing] = useState(false)
  const { getMyActualWorkByTaskUuidAndDate } = useGetTaskActualWork()
  const { saveMyTaskActualWork } = useSaveTaskActualWork()

  const workDateLabel = useMemo(
    () => intl.formatMessage({ id: 'taskActualWorkList.header.date' }),
    []
  )
  const workHourLabel = useMemo(
    () => intl.formatMessage({ id: 'taskActualWorkList.header.hour' }),
    []
  )
  const { parse, round, parseFullToHalf } = useNumberParser()

  const refreshWorkHour = useCallback(
    async (taskUuid: UUIDV2, workDate: DateV2 | undefined) => {
      setProcessing(true)
      if (workDate) {
        const taskActualWork = await getMyActualWorkByTaskUuidAndDate(
          taskUuid,
          workDate
        )
        setWorkHour(taskActualWork.hour + '')
      } else {
        setWorkHour('')
      }
      setProcessing(false)
    },
    [getMyActualWorkByTaskUuidAndDate]
  )
  useEffect(() => {
    refreshWorkHour(taskUuid, workDate)
  }, [taskUuid, workDate, refreshWorkHour])

  const onChangeWorkDate = useCallback(newValue => {
    setWorkDate(newValue)
  }, [])
  const onChangeWorkHour = useCallback(
    newValue => {
      if (isComposing) return
      const parsedValue = parseFullToHalf(newValue)
      setWorkHour(parsedValue)
    },
    [parseFullToHalf, isComposing]
  )
  const onCompositionStart = useCallback(() => {
    setIsComposing(true)
  }, [])
  const onCompositionEnd = useCallback(() => {
    setIsComposing(false)
  }, [])
  const parseHour = useCallback(
    (hour: string | undefined): string | undefined => {
      if (hour === undefined) {
        return '0'
      }
      const normalized = parse(hour)
      let newWorkHour = round(normalized)
      if (newWorkHour !== undefined && (0 > newWorkHour || 24 < newWorkHour)) {
        newWorkHour = 0
      }
      return newWorkHour + ''
    },
    [parse, round]
  )

  const onBlur = useCallback(() => {
    setWorkHour(parseHour(workHour))
  }, [workHour, parseHour])

  const submit = useCallback(() => {
    if (processing || !workDate) return
    setProcessing(true)

    const normalized = parse(workHour)
    let newWorkHour = round(normalized)
    if (newWorkHour !== undefined && (0 > newWorkHour || 24 < newWorkHour)) {
      newWorkHour = 0
    }

    saveMyTaskActualWork(projectUuid, wbsItemType, {
      taskUuid,
      hour: newWorkHour || 0,
      actualDate: workDate,
    })
      .then(() => {
        dispatch(
          addScreenMessage('wbsItem', {
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({ id: 'registration.complete' }),
          })
        )
        closeDialog()
      })
      .catch(error => {
        if (error.code === ErrorCode.BUSINESS_ERROR) {
          store.dispatch(
            addScreenMessage('wbsItem', {
              type: MessageLevel.WARN,
              title: error.detail,
              text: error.detail,
            })
          )
        } else {
          throw error
        }
      })
      .finally(() => {
        setProcessing(false)
      })
  }, [
    processing,
    parse,
    workHour,
    round,
    saveMyTaskActualWork,
    taskUuid,
    workDate,
    dispatch,
    closeDialog,
  ])

  useKeyBind(
    [{ key: KEY_SAVE, fn: submit, stopDefaultBehavior: true }],
    [submit]
  )

  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogContent>
        <TableHeader title="作業実績" />
        <TableRow>
          <DateCell
            label={workDateLabel}
            cellWidth="full"
            value={workDate}
            onChange={onChangeWorkDate}
          />
        </TableRow>
        <TableRow>
          <TextCell
            label={workHourLabel}
            cellWidth="full"
            value={workHour}
            onChange={onChangeWorkHour}
            onBlur={onBlur}
            onCompositionStart={onCompositionStart}
            onCompositionEnd={onCompositionEnd}
          />
        </TableRow>
      </DialogContent>
      <DialogActions>
        <SubmitButton onClick={submit} disabled={processing} />
        <CloseButton onClick={closeDialog} />
      </DialogActions>
    </Dialog>
  )
}

export default TaskActualWorkRegistrationDialog
